'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const segmentedButtonVariants = cva(
  'inline-flex items-center rounded-[5px] bg-muted p-1 text-muted-foreground',
  {
    variants: {
      size: {
        default: 'h-9',
        sm: 'h-8',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const segmentedItemVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded px-4 text-xs font-normal transition-all disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  {
    variants: {
      size: {
        default: 'min-w-[80px] py-1.5',
        sm: 'min-w-[60px] py-1',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

interface SegmentedButtonProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    VariantProps<typeof segmentedButtonVariants> {}

const SegmentedButton = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  SegmentedButtonProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} className={className} {...props} />
));
SegmentedButton.displayName = 'SegmentedButton';

interface SegmentedButtonListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof segmentedButtonVariants> {}

const SegmentedButtonList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  SegmentedButtonListProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(segmentedButtonVariants({ size }), className)}
    {...props}
  />
));
SegmentedButtonList.displayName = 'SegmentedButtonList';

interface SegmentedButtonItemProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof segmentedItemVariants> {}

const SegmentedButtonItem = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  SegmentedButtonItemProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(segmentedItemVariants({ size }), className)}
    {...props}
  />
));
SegmentedButtonItem.displayName = 'SegmentedButtonItem';

const SegmentedButtonContent = TabsPrimitive.Content;

export {
  SegmentedButton,
  SegmentedButtonList,
  SegmentedButtonItem,
  SegmentedButtonContent,
  segmentedButtonVariants,
  segmentedItemVariants,
};
